/**
 * ReferenceDataService — HttpService wrapper for the bid reference-data catalogs + JSON import (P3).
 *
 * Plain exported functions rather than the static-class shape used by e.g. ChartOfAccountsService,
 * to match its immediate sibling `BidSheets/bidService.ts` (same feature, same folder).
 *
 * Every catalog exposes the identical surface, so one set of functions takes the catalog's URL from
 * `catalogDescriptors.ts` instead of there being seven near-identical services.
 */
import HttpService from '../../AppSettings/services/HttpService';

const http = new HttpService();

/** A catalog row as the API returns it — columns vary per catalog, so it stays open. */
export interface CatalogItem {
  id: string;
  active: boolean;
  [key: string]: unknown;
}

export interface ImportRowError {
  index: number;
  row: unknown;
  errors: string[];
}

export interface ImportPreview {
  kind: string;
  key: string;
  valid: Array<{ index: number; key: unknown; row: Record<string, unknown> }>;
  invalid: ImportRowError[];
  counts: { total: number; valid: number; invalid: number };
}

export interface ImportResult {
  kind: string;
  inserted: number;
  updated: number;
  skipped: number;
  invalid: ImportRowError[];
  counts: { total: number; valid: number; invalid: number };
}

/**
 * A failed catalog call, carrying the HTTP status so callers can tell a 409 (the row is still
 * referenced — deactivate instead) from a generic failure and say something useful.
 */
export class ReferenceDataError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ReferenceDataError';
    this.status = status;
  }
}

/**
 * Normalise a failed call into a ReferenceDataError.
 *
 * HttpService.request does NOT rethrow the axios error — it throws a fresh Error carrying
 * `.status` / `.data` (see AppSettings/services/HttpService.ts). Reading `.response` therefore
 * never works here; that is checked first only so a directly-thrown axios error still resolves.
 *
 * Two body shapes arrive: the standard `{successful, error, ...}` envelope, and `validate_json`'s
 * RAW `{errors: {field: [msg, ...]}}` (helpers/schemas.py) which has no `error` key at all — that
 * one is flattened to "field: msg" so a validation 400 doesn't surface as "Request failed with
 * status code 400".
 */
export function toError(err: any): ReferenceDataError {
  const status = err?.status ?? err?.response?.status ?? 0;
  const body = err?.data ?? err?.response?.data;

  let message: string | undefined;
  if (typeof body?.error === 'string') {
    message = body.error;
  } else if (body?.errors && typeof body.errors === 'object') {
    message = Object.entries(body.errors as Record<string, unknown>)
      .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : String(msgs)}`)
      .join('; ');
  }

  return new ReferenceDataError(message || err?.message || 'Request failed', status);
}

async function call<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    throw toError(err);
  }
}

// ---------------------------------------------------------------------------
// Catalog CRUD
// ---------------------------------------------------------------------------

export async function listCatalog(url: string, activeOnly = false): Promise<CatalogItem[]> {
  const res = await call(() => http.get<any>(`${url}${activeOnly ? '?active_only=true' : ''}`));
  return res?.data?.items ?? [];
}

// ---------------------------------------------------------------------------
// The fleet registry (Build U, plan team-bid-parity-and-fleet-registry) — every
// asset-tagged unit with its live-assignment rollup, plus untagged quantity pools.
// Money-free by construction; the GET is ADMIN_CATALOGS-gated (stricter than the
// sibling catalog reads — the response carries matter names, plan decision 7).
// ---------------------------------------------------------------------------

export interface FleetAssignment {
  matterid: string;
  matter_name: string | null;
  phase_id: string;
  phase_name: string | null;
  /** 0028's derived lifecycle: 'future' | 'in_progress' | 'complete'. */
  phase_status: string;
}

export interface FleetUnit {
  id: string;
  name: string | null;
  asset_tag: string | null;
  equipment_item_id: string | null;
  active: boolean;
  sheet_count: number;
  last_seen: string | null;
  assignments: FleetAssignment[];
}

export interface FleetPool {
  equipment_item_id: string | null;
  quantity: string;
  rows: number;
}

export interface FleetRegistry {
  units: FleetUnit[];
  untagged_pools: FleetPool[];
}

export async function fetchFleetRegistry(): Promise<FleetRegistry> {
  const res = await call(() => http.get<any>('/inventory-items/fleet'));
  return res?.data ?? { units: [], untagged_pools: [] };
}

export async function createCatalogItem(
  url: string,
  payload: Record<string, unknown>,
): Promise<CatalogItem> {
  const res = await call(() => http.post<any>(url, payload));
  return res?.data?.item;
}

export async function updateCatalogItem(
  url: string,
  id: string,
  payload: Record<string, unknown>,
): Promise<CatalogItem> {
  const res = await call(() => http.put<any>(`${url}/${id}`, payload));
  return res?.data?.item;
}

export async function deactivateCatalogItem(url: string, id: string): Promise<CatalogItem> {
  const res = await call(() => http.post<any>(`${url}/${id}/deactivate`));
  return res?.data?.item;
}

export async function reactivateCatalogItem(url: string, id: string): Promise<CatalogItem> {
  const res = await call(() => http.post<any>(`${url}/${id}/reactivate`));
  return res?.data?.item;
}

/** Hard delete. Throws ReferenceDataError with status 409 when the row is still referenced. */
export async function deleteCatalogItem(url: string, id: string): Promise<void> {
  await call(() => http.delete<any>(`${url}/${id}`));
}

// ---------------------------------------------------------------------------
// JSON import (preview -> commit)
// ---------------------------------------------------------------------------

/** Validate an upload without persisting anything. */
export async function previewImport(kind: string, rows: unknown[]): Promise<ImportPreview> {
  const res = await call(() => http.post<any>(`/reference-data/${kind}/import/preview`, rows));
  return res?.data;
}

/** Persist the valid rows (idempotent upsert on the catalog's natural key). */
export async function commitImport(kind: string, rows: unknown[]): Promise<ImportResult> {
  const res = await call(() => http.post<any>(`/reference-data/${kind}/import/commit`, rows));
  return res?.data;
}

/**
 * Parse an uploaded .json file into rows. Accepts a bare array (what an exported catalog looks
 * like) or an object wrapping one, matching what the endpoint accepts. Throws a readable
 * ReferenceDataError rather than a raw SyntaxError so the screen can show it as-is.
 */
export async function readRowsFromFile(file: File): Promise<unknown[]> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    throw new ReferenceDataError(`${file.name} is not valid JSON.`, 0);
  }
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') {
    for (const key of ['rows', 'items', 'data']) {
      const value = (parsed as Record<string, unknown>)[key];
      if (Array.isArray(value)) return value;
    }
  }
  throw new ReferenceDataError(
    `${file.name} must contain a JSON array of rows, or an object with a "rows" array.`,
    0,
  );
}

// ── Audit log (gap review 1.7) ──────────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  entity_type: string;
  entity_id: string | null;
  action: string;
  changed_fields: Record<string, unknown> | null;
  performed_by: string;
  performed_at: string;
  notes: string | null;
}

/** Latest audit rows, newest first — ADMIN_CATALOGS-gated and money-stripped server-side. */
export async function listAuditEntries(entityType?: string, limit = 100): Promise<AuditEntry[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (entityType) params.set('entity_type', entityType);
  try {
    const res = await http.get<any>(`/bid-reference-audit-log?${params.toString()}`);
    return (res?.data?.entries ?? []) as AuditEntry[];
  } catch (err) {
    throw toError(err);
  }
}
